// 歐盟 / 英國市場閘門（SPEC #product-security-market-gate，Eddy 2026-10-21 選單）：Kinio 自己發的下載依連線 IP 的國家
// 不對歐盟 27 國、EEA（NO / IS / LI）與英國提供，下載處顯示「目前未在您的地區提供」。
// 判定在平台（不帶 token）：GET {plane api}/v1/market/availability?product=<kmgr-agent | kmgrctl | kinio-station-desktop>
//   → {available, country, message?, messages?}
// 拿不到（沒有該 env 的 plane、網路錯、逾時、格式不對）→ 當作提供（平台沒有 IP 資料時也是放行；畫面不因為查不到就擋）。
import { useQuery } from '@tanstack/react-query';
import { planeBase } from '@kinio/web-sdk';
import type { AppConfig } from './appConfig';

export type MarketProduct = 'kmgr-agent' | 'kmgrctl' | 'kinio-station-desktop';

export interface MarketAvailability {
  available: boolean;
  country: string;
  /** 平台給的四語說明（zh-Hant / zh-Hans / en / ja）；沒有就用畫面自己的字串。 */
  messages?: Record<string, string>;
}

export const MARKET_OPEN: MarketAvailability = Object.freeze({ available: true, country: '' }) as MarketAvailability;
export const MARKET_TIMEOUT_MS = 3000;

export async function fetchMarketAvailability(
  cfg: Pick<AppConfig, 'env' | 'planes'>,
  product: MarketProduct,
  fetchImpl: typeof fetch = fetch,
  timeoutMs = MARKET_TIMEOUT_MS,
): Promise<MarketAvailability> {
  const api = cfg.planes?.[cfg.env];
  if (!api) return MARKET_OPEN;
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetchImpl(`${planeBase(api)}/v1/market/availability?product=${encodeURIComponent(product)}`, {
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      signal: ctl.signal,
    });
    if (!res.ok) return MARKET_OPEN;
    const body = (await res.json()) as { available?: unknown; country?: unknown; messages?: unknown } | null;
    if (body?.available !== false) return MARKET_OPEN;
    const msgs = body.messages && typeof body.messages === 'object' ? (body.messages as Record<string, string>) : undefined;
    return { available: false, country: typeof body.country === 'string' ? body.country : '', messages: msgs };
  } catch {
    return MARKET_OPEN;
  } finally {
    clearTimeout(timer);
  }
}

/** 下載處用：這個產品在使用者目前的地區提供嗎（查詢中當作提供，不閃一下擋住再放開）。 */
export function useMarketAvailability(cfg: Pick<AppConfig, 'env' | 'planes'>, product: MarketProduct): MarketAvailability {
  const q = useQuery({
    queryKey: ['market-availability', cfg.env, product],
    queryFn: () => fetchMarketAvailability(cfg, product),
    staleTime: 10 * 60_000,
    retry: false,
  });
  return q.data ?? MARKET_OPEN;
}
